import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, BellRing, ChefHat, CircleDollarSign, Clock, PackageX, Printer, Timer, Utensils } from 'lucide-react';
import { PAYMENT_METHOD_LABEL } from '@garlic/shared';
import { api, errorMessage } from '../../lib/api';
import { formatBRL } from '../../lib/format';
import { Alert, Badge, Card, CardHeader, Input, PageHeader, Select, Spinner, cx } from '../../ui/primitives';
import type { TenantRestaurant } from '../TenantApp';
import { staffPath } from '../../lib/paths';

type Dash = {
  date: string; isToday: boolean; stations: { id: string; name: string }[];
  production: { received: number; inPreparation: number; ready: number; delivered: number; late: number | null; avgPrepMinutes: number | null; byStation: { stationId: string; name: string; avgPrepMinutes: number | null; ticketsReady: number }[] };
  floor: { tablesTotal: number; occupied: number; free: number; openSessions: number; billRequested: number };
  orders: { count: number; cancelledItems: number };
  financial: {
    consumedCents: number; averageOrderCents: number; receivedCents: number; receivedByMethod: { method: string; amountCents: number }[];
    digitalPendingCents: number; digitalPendingCount: number; openAccountsPartiallyPaid: number; openAccountsSettled: number;
  } | null;
  topProducts: { name: string; quantity: number }[];
  unavailableProducts: { id: string; name: string; reason: 'paused' | 'sold_out' }[];
  printing: { failedToday: number; pending: number; agentsOnline: number; agentsTotal: number; byStation: { stationId: string; name: string; state: string; failedRecent: number }[] };
};

function Tile({ icon, label, value, hint, tone }: { icon: ReactNode; label: string; value: ReactNode; hint?: ReactNode; tone?: 'danger' | 'warning' }) {
  return (
    <Card className={cx('min-w-0 p-3 sm:p-4', tone === 'danger' && 'border-danger-600/40 bg-danger-50', tone === 'warning' && 'border-warn-600/40 bg-warn-50')}>
      <p className="flex items-center gap-1.5 text-xs font-medium text-ink-3">{icon} {label}</p>
      {/* No celular, valores como "R$ 20.802,00" (espaço inquebrável do Intl) não cabem em duas colunas com text-3xl. */}
      <p className="mt-1.5 font-display text-xl font-semibold tabular-nums [overflow-wrap:anywhere] sm:text-3xl">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-ink-3">{hint}</p>}
    </Card>
  );
}

export function StaffDashboard({ restaurant, name }: { restaurant: TenantRestaurant; name: string }) {
  const [date, setDate] = useState('');
  const [stationId, setStationId] = useState('');
  const params = new URLSearchParams({ ...(date ? { date } : {}), ...(stationId ? { stationId } : {}) });
  const q = useQuery({ queryKey: ['staff-dashboard', date, stationId], queryFn: () => api<Dash>(`/api/staff/dashboard?${params}`), refetchInterval: 60_000 });
  const d = q.data;
  return (
    <>
      <PageHeader
        title={`Olá, ${name.split(' ')[0]}`}
        description={d ? (d.isToday ? 'Operação de hoje, em tempo real.' : `Resumo de ${new Date(`${d.date}T12:00:00`).toLocaleDateString('pt-BR')}.`) : undefined}
        action={
          <div className="flex flex-wrap gap-2">
            <Input type="date" aria-label="Data" className="w-40" value={date || d?.date || ''} onChange={(e) => setDate(e.target.value)} />
            <Select aria-label="Setor" className="w-40" value={stationId} onChange={(e) => setStationId(e.target.value)}>
              <option value="">Todos os setores</option>
              {d?.stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </div>
        }
      />
      {restaurant.status === 'ONBOARDING' && <div className="mb-6"><Alert tone="warning" title="Restaurante em implantação">O cardápio público mostra “em breve” até o Garlic ativar o estabelecimento.</Alert></div>}
      {q.isPending && <Spinner />}
      {q.isError && <Alert tone="danger">{errorMessage(q.error)}</Alert>}
      {d && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Tile icon={<ChefHat className="size-3.5" />} label="Recebidos" value={d.production.received} />
            <Tile icon={<Timer className="size-3.5" />} label="Em preparo" value={d.production.inPreparation} />
            <Tile icon={<Utensils className="size-3.5" />} label="Prontos p/ entregar" value={d.production.ready} tone={d.production.ready > 0 ? 'warning' : undefined} />
            <Tile icon={<AlertTriangle className="size-3.5" />} label="Atrasados agora" value={d.production.late ?? '—'} tone={d.production.late ? 'danger' : undefined} />
            <Tile icon={<Clock className="size-3.5" />} label="Tempo médio de preparo" value={d.production.avgPrepMinutes != null ? `${d.production.avgPrepMinutes} min` : '—'} hint="do pedido até “pronto”" />
            <Tile icon={<Utensils className="size-3.5" />} label="Mesas ocupadas" value={`${d.floor.occupied}/${d.floor.tablesTotal}`} hint={`${d.floor.free} livre(s)`} />
            <Tile icon={<BellRing className="size-3.5" />} label="Pedindo a conta" value={d.floor.billRequested} tone={d.floor.billRequested ? 'warning' : undefined} hint={`${d.floor.openSessions} comanda(s) abertas`} />
            {d.financial ? (
              <Tile icon={<CircleDollarSign className="size-3.5" />} label="Consumo lançado" value={formatBRL(d.financial.consumedCents)} hint={`${d.orders.count} pedido(s) · ticket médio ${formatBRL(d.financial.averageOrderCents)}`} />
            ) : (
              <Tile icon={<CircleDollarSign className="size-3.5" />} label="Pedidos" value={d.orders.count} hint={`${d.orders.cancelledItems} item(ns) cancelado(s)`} />
            )}
          </div>
          {d.financial && (
            <>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Tile icon={<CircleDollarSign className="size-3.5" />} label="Recebido (confirmado)" value={formatBRL(d.financial.receivedCents)} hint={d.financial.receivedByMethod.map((m) => `${PAYMENT_METHOD_LABEL[m.method] ?? m.method}: ${formatBRL(m.amountCents)}`).join(' · ') || 'Nenhum pagamento no período'} />
                <Tile icon={<Clock className="size-3.5" />} label="Pagamentos digitais pendentes" value={formatBRL(d.financial.digitalPendingCents)} hint={`${d.financial.digitalPendingCount} aguardando o Mercado Pago`} tone={d.financial.digitalPendingCount ? 'warning' : undefined} />
                <Tile icon={<Utensils className="size-3.5" />} label="Contas parcialmente pagas" value={d.financial.openAccountsPartiallyPaid} hint="comandas abertas" />
                <Tile icon={<Utensils className="size-3.5" />} label="Contas quitadas (abertas)" value={d.financial.openAccountsSettled} hint="aguardando encerramento" />
              </div>
              <p className="-mt-3 text-xs text-ink-3">“Consumo lançado” soma os itens pedidos (sem cancelados). “Recebido” só conta pagamentos confirmados — pendentes e recusados nunca entram como receita. Tarifas do Mercado Pago não são estimadas.</p>
            </>
          )}

          <div className="grid gap-6 lg:grid-cols-3">
            <Card>
              <CardHeader title="Por setor" />
              <ul className="divide-y divide-line text-sm">
                {d.production.byStation.map((s) => {
                  const p = d.printing.byStation.find((x) => x.stationId === s.stationId);
                  return (
                    <li key={s.stationId} className="flex items-center justify-between gap-2 px-5 py-3">
                      <span>
                        <Link to={staffPath(`/producao/${s.stationId}`)} className="font-medium hover:underline">{s.name}</Link>
                        <span className="block text-xs text-ink-3">{s.ticketsReady} pronta(s) · média {s.avgPrepMinutes ?? '—'} min</span>
                      </span>
                      {p?.state === 'offline' && <Badge tone="danger" dot>Impressão offline</Badge>}
                      {p?.state === 'failures' && <Badge tone="warning" dot>{p.failedRecent} falha(s)</Badge>}
                      {p?.state === 'ok' && <Badge tone="success" dot>Impressão ok</Badge>}
                      {p?.state === 'none' && <Badge>Só tela</Badge>}
                    </li>
                  );
                })}
              </ul>
            </Card>
            <Card>
              <CardHeader title="Mais pedidos" />
              {d.topProducts.length === 0 ? <p className="px-5 py-4 text-sm text-ink-3">Nenhum pedido no período.</p> : (
                <ol className="divide-y divide-line text-sm">
                  {d.topProducts.map((p, i) => (
                    <li key={p.name} className="flex justify-between gap-2 px-5 py-2.5"><span><span className="mr-2 text-ink-3">{i + 1}.</span>{p.name}</span><span className="font-semibold tabular-nums">{p.quantity}</span></li>
                  ))}
                </ol>
              )}
            </Card>
            <Card>
              <CardHeader title="Atenção" />
              <ul className="space-y-2 p-5 text-sm">
                <li className="flex items-start gap-2">
                  <Printer className="mt-0.5 size-4 text-ink-3" />
                  <span>
                    {d.printing.agentsTotal === 0 ? 'Nenhum agente de impressão pareado.' : `${d.printing.agentsOnline}/${d.printing.agentsTotal} agente(s) online.`}
                    {d.printing.failedToday > 0 && <span className="block font-medium text-danger-600">{d.printing.failedToday} impressão(ões) falharam no dia.</span>}
                    {d.printing.pending > 0 && <span className="block text-ink-3">{d.printing.pending} na fila agora.</span>}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <PackageX className="mt-0.5 size-4 text-ink-3" />
                  <span>
                    {d.unavailableProducts.length === 0 ? 'Todos os produtos disponíveis.' : `${d.unavailableProducts.length} produto(s) indisponível(is):`}
                    {d.unavailableProducts.slice(0, 6).map((p) => <span key={p.id} className="block text-ink-3">{p.name} — {p.reason === 'paused' ? 'pausado' : 'esgotado'}</span>)}
                  </span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      )}
    </>
  );
}
